export type Environment = 'dev' | 'prod';

// Explicit list of known clubs/tenants. The bridge only `postMessage`s
// credentials to origins whose tenant slug is in this list. Update when
// onboarding or offboarding a club. An implicit regex-only allowlist would
// trust any DNS that happens to resolve, including dangling subdomains.
const KNOWN_DEV_CLUBS: ReadonlySet<string> = new Set([
  'bobba',
  'r2d2',
  'vader',
  'conrad',
  'jarjar',
]);

const KNOWN_PROD_CLUBS: ReadonlySet<string> = new Set([
  'fc-dallas',
  'juventus',
  'dc-united',
  'hta',
  'mta',
  'arisl',
  'solo',
  'hpt',
]);

const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]*$/;

interface MatchResult {
  /** True if the origin's hostname follows the right shape for the env. */
  shapeMatches: boolean;
  /** Tenant slug extracted from the hostname, if any. */
  slug?: string;
}

function matchDevHostname(hostname: string): MatchResult {
  if (hostname === 'localhost') {
    return { shapeMatches: true };
  }
  // Expect <slug>-app.dev.ondeo.store
  const suffix = '-app.dev.ondeo.store';
  if (!hostname.endsWith(suffix)) return { shapeMatches: false };
  const slug = hostname.slice(0, -suffix.length);
  if (!SLUG_PATTERN.test(slug)) return { shapeMatches: false };
  return { shapeMatches: true, slug };
}

function matchProdHostname(hostname: string): MatchResult {
  if (hostname === 'themarquee.ai' || hostname === 'www.themarquee.ai') {
    return { shapeMatches: true };
  }
  // Expect app.<slug>.themarquee.ai
  const suffix = '.themarquee.ai';
  if (!hostname.endsWith(suffix)) return { shapeMatches: false };
  const head = hostname.slice(0, -suffix.length); // "app.<slug>"
  if (!head.startsWith('app.')) return { shapeMatches: false };
  const slug = head.slice('app.'.length);
  if (!SLUG_PATTERN.test(slug)) return { shapeMatches: false };
  return { shapeMatches: true, slug };
}

export function isAllowedOrigin(origin: string, env: Environment): boolean {
  if (!origin) return false;
  let parsed: URL;
  try {
    parsed = new URL(origin);
  } catch {
    return false;
  }
  // Defense in depth: reject anything with a path/query/fragment beyond the bare origin.
  // (parsed.origin already strips these, but we re-check the raw input.)
  if (parsed.pathname !== '/' && parsed.pathname !== '') return false;
  if (parsed.search || parsed.hash) return false;

  // Scheme + port checks per environment.
  if (env === 'dev') {
    const isLocalhost = parsed.hostname === 'localhost';
    if (isLocalhost) {
      if (parsed.protocol !== 'http:') return false;
    } else {
      if (parsed.protocol !== 'https:') return false;
      if (parsed.port !== '' && parsed.port !== '443') return false;
    }
    const m = matchDevHostname(parsed.hostname);
    if (!m.shapeMatches) return false;
    if (m.slug === undefined) return true; // localhost
    return KNOWN_DEV_CLUBS.has(m.slug);
  }

  // prod
  if (parsed.protocol !== 'https:') return false;
  if (parsed.port !== '' && parsed.port !== '443') return false;
  const m = matchProdHostname(parsed.hostname);
  if (!m.shapeMatches) return false;
  if (m.slug === undefined) return true; // themarquee.ai or www
  return KNOWN_PROD_CLUBS.has(m.slug);
}
